import { DollarSignIcon, Shirt } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import { twJoin } from 'tailwind-merge';

import { FormCheckbox, FormField, FormInput, FormNumberInput } from '@/_components';
import {
  CardContainer,
  FormCurrencyInput,
  FormLayout,
  SectionHeading,
  SelectorWithAvatarContainer,
} from '@/_containers';
import { BrandsAsyncSelect } from '@/_pages/settings/brands/shared';
import { InventoryCategoriesSelect } from '@/_pages/settings/inventory-categories/shared/components';
import type { InventoryFormSchema } from '@/_pages/settings/inventory-kits/shared/schemas/inventory';
import { InventoryInventoryVolumesSelect } from '@/_pages/settings/inventory-volumes/shared/components';
import { nameResolver, showFormField } from '@/_shared';

import { InventoryCombo, MultiBinLocationsSection } from '../components';
import { MultiInventoriesSection } from '../components/multi-inventories-section';
import { SKUsForm } from '../components/skus/form-input-multiple-skus';

export function FormFields({
  form,
  isEditable = true,
  isInventoryParent,
  autoFocus = false,
}: {
  form: UseFormReturn<InventoryFormSchema>;
  isEditable?: boolean;
  isInventoryParent?: boolean;
  autoFocus?: boolean;
}) {
  const [id, inventory_volume, parent, children] = form.watch(['id', 'inventory_volume', 'parent', 'children']);

  return (
    <>
      <CardContainer>
        <FormLayout>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormInput
                formItemClassName="col-span-12"
                label="Name (required)"
                {...field}
                disabled={!isEditable}
                autoFocus={autoFocus}
              />
            )}
          />

          <FormField
            control={form.control}
            name="inventory_category"
            render={({ field }) => (
              <InventoryCategoriesSelect
                disabled={!isEditable}
                formItemClassName={twJoin(
                  'col-span-12 sm:col-span-6',
                  isInventoryParent ? 'lg:col-span-6' : 'lg:col-span-3'
                )}
                value={field.value}
                label="Category (required)"
                onValueChange={(e) => {
                  form.setValue('track_stock_quantities', !!e?.track_stock_quantities);
                  form.setValue('track_serial_numbers', !!e?.track_serial_numbers);
                  field.onChange(e);
                }}
              />
            )}
          />

          {!isInventoryParent && (
            <>
              <div className={`col-span-12 sm:col-span-6 lg:col-span-3`}>
                <SKUsForm isEditable={isEditable} />
              </div>

              <FormCurrencyInput
                isEditable={isEditable}
                name="last_order_cost"
                formItemClassName="col-span-12 sm:col-span-6 lg:col-span-3"
                label="Last Order Cost"
              />

              <FormField
                control={form.control}
                name="weight"
                render={({ field }) => (
                  <FormNumberInput
                    formItemClassName="col-span-12 sm:col-span-6 lg:col-span-3"
                    label="Weight"
                    {...field}
                    disabled={!isEditable}
                    suffix=" lbs"
                    thousandSeparator
                    valueIsNumericString
                    fixedDecimalScale
                    value={field.value}
                    onValueChange={(s) => {
                      field.onChange(s.value);
                    }}
                  />
                )}
              />
            </>
          )}
          <div className="col-span-12 sm:col-span-6 lg:col-span-6">
            <FormField
              control={form.control}
              name="brand"
              render={({ field: { value, onChange } }) => (
                <SelectorWithAvatarContainer
                  disabled={!isEditable}
                  src={value?.icon?.at(0)?.url || ''}
                  label={nameResolver(value?.name, undefined, 'Brand')}
                  name={value?.name ?? ''}
                  className="lg:flex-row"
                >
                  <BrandsAsyncSelect disabled={!isEditable} label="Brand" value={value} onValueChange={onChange} />
                </SelectorWithAvatarContainer>
              )}
            />
          </div>

          {!isInventoryParent && (
            <>
              <FormField
                control={form.control}
                name="manufacturer"
                render={({ field }) => (
                  <FormInput
                    formItemClassName="col-span-12 sm:col-span-6 lg:col-span-3"
                    label="Manufacturer"
                    {...field}
                    disabled={!isEditable}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="manufacturer_id"
                render={({ field }) => (
                  <FormInput
                    formItemClassName="col-span-12 sm:col-span-6 lg:col-span-3"
                    label="Manufacturer ID"
                    {...field}
                    disabled={!isEditable}
                  />
                )}
              />

              <div className="col-span-12 grid grid-cols-12 gap-6">
                <FormField
                  control={form.control}
                  name="track_stock_quantities"
                  render={({ field }) => (
                    <FormCheckbox
                      label="Track Stock Quantities"
                      disabled={!isEditable}
                      checked={!!field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                      }}
                      formItemClassName="col-span-6 xl:col-span-4"
                    />
                  )}
                />

                <FormField
                  control={form.control}
                  name="track_serial_numbers"
                  render={({ field }) => (
                    <FormCheckbox
                      label="Track Serial Numbers"
                      disabled={!isEditable}
                      checked={!!field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                      }}
                      formItemClassName="col-span-6 xl:col-span-4"
                    />
                  )}
                />

                <FormField
                  control={form.control}
                  name="discontinued"
                  render={({ field }) => (
                    <FormCheckbox
                      label="Discontinued"
                      disabled={!isEditable}
                      checked={!!field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                      }}
                      formItemClassName="col-span-6 xl:col-span-4"
                    />
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="parent"
                render={({ field }) => (
                  <InventoryCombo
                    formItemClassName="col-span-12"
                    label="Parent"
                    value={field.value}
                    onValueChange={(v) => {
                      field.onChange(v);
                      form.setValue('children', [], { shouldDirty: true });
                    }}
                    disabled={!isEditable}
                    filterId={id}
                  />
                )}
              />
            </>
          )}
        </FormLayout>
      </CardContainer>

      {!parent && isInventoryParent && showFormField(isEditable, (children?.length ?? 0) > 0) && (
        <CardContainer>
          <MultiInventoriesSection
            items={children ?? []}
            control={form.control}
            fieldName="children"
            isEditable={isEditable}
            sectionTitle="Sub Items"
            title="Inventory Items"
            icon={Shirt}
            sortable
            filterId={id}
          />
        </CardContainer>
      )}

      {!isInventoryParent && (
        <>
          <CardContainer>
            <SectionHeading text="Stock" icon={Shirt} />
            <FormLayout className="mt-8">
              <FormField
                control={form.control}
                name="current_stock"
                render={({ field }) => (
                  <FormNumberInput
                    formItemClassName="col-span-12 sm:col-span-6 md:col-span-4"
                    label="Current Stock"
                    {...field}
                    thousandSeparator
                    valueIsNumericString
                    fixedDecimalScale
                    disabled={!isEditable}
                    value={field.value}
                    onValueChange={(s) => {
                      field.onChange(s.value);
                    }}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="committed"
                render={({ field }) => (
                  <FormNumberInput
                    formItemClassName="col-span-12 sm:col-span-6 md:col-span-4"
                    label="Committed"
                    {...field}
                    thousandSeparator
                    valueIsNumericString
                    fixedDecimalScale
                    disabled
                    value={field.value}
                    onValueChange={(s) => {
                      field.onChange(s.value);
                    }}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="minimum_stock"
                render={({ field }) => (
                  <FormNumberInput
                    formItemClassName="col-span-12 sm:col-span-6 md:col-span-4"
                    label="Minimum Stock"
                    {...field}
                    thousandSeparator
                    valueIsNumericString
                    fixedDecimalScale
                    disabled={!isEditable}
                    value={field.value}
                    onValueChange={(s) => {
                      field.onChange(s.value);
                    }}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="maximum_stock"
                render={({ field }) => (
                  <FormNumberInput
                    formItemClassName="col-span-12 sm:col-span-6 md:col-span-4"
                    label="Maximum Stock"
                    {...field}
                    thousandSeparator
                    valueIsNumericString
                    fixedDecimalScale
                    disabled={!isEditable}
                    value={field.value}
                    onValueChange={(s) => {
                      field.onChange(s.value);
                    }}
                  />
                )}
              />
            </FormLayout>

            <MultiBinLocationsSection disabled={!isEditable} />
          </CardContainer>

          <CardContainer>
            <SectionHeading text="Purchasing" icon={DollarSignIcon} />
            <FormLayout className="mt-8">
              <FormField
                control={form.control}
                name="inventory_volume"
                render={({ field }) => (
                  <InventoryInventoryVolumesSelect
                    disabled={!isEditable}
                    formItemClassName="col-span-12 sm:col-span-6 md:col-span-4"
                    value={field.value}
                    label="Inventory Volume"
                    onValueChange={field.onChange}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="units_per_order"
                render={({ field }) => (
                  <FormNumberInput
                    formItemClassName="col-span-12 sm:col-span-6 md:col-span-4"
                    label="Unit Per Order"
                    {...field}
                    thousandSeparator
                    valueIsNumericString
                    fixedDecimalScale
                    disabled
                    value={inventory_volume?.quantity ?? '0'}
                    onValueChange={(s) => {
                      field.onChange(s.value);
                    }}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="minimum_order"
                render={({ field }) => (
                  <FormNumberInput
                    formItemClassName="col-span-12 sm:col-span-6 md:col-span-4"
                    label="Minimum Order"
                    {...field}
                    thousandSeparator
                    valueIsNumericString
                    fixedDecimalScale
                    disabled={!isEditable}
                    value={field.value}
                    onValueChange={(s) => {
                      field.onChange(s.value);
                    }}
                  />
                )}
              />
            </FormLayout>
          </CardContainer>
        </>
      )}
    </>
  );
}
